import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useFeatures } from '@atc/features';

import { esntialEligibilityFetcher } from 'reaxl-ecommerce';

import getEsntialEligibilityConfig from '@/utilities/getEsntialEligibilityConfig.js';

import inventoryDuck from '@/ducks/inventoryDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';

/**
 * Custom hook to handle Esntial eligibility checking on SRP
 * @param {Object} listing - The vehicle listing object
 * @param {boolean} isSponsoredExperience - Whether this is a sponsored experience
 * @returns {Object} { isEligible, isLoading, error }
 */
export default function useEsntialEligibility(listing, isSponsoredExperience) {
    const [isEligible, setIsEligible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const dispatch = useDispatch();
    const {
        esntial_eligibility_v2: [enableV2],
    } = useFeatures(['esntial_eligibility_v2']);

    const { esntial, id, vin } = listing || {};
    const existingDeal = useSelector((state) => paymentsDuck?.selectors?.getDealByListingVin(state, vin)) || {};

    useEffect(() => {
        if (!isSponsoredExperience || !listing || !esntial?.ecommWebsite) {
            setIsEligible(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        // Create payload and endpoint configuration using utility function
        const { payloadItems, eligibilityURL, isV2Enabled } = getEsntialEligibilityConfig(listing, enableV2);

        const versionSuffix = isV2Enabled ? ' V2' : '';
        const dataKey = isV2Enabled ? 'payloadItems' : 'vin';
        const dataValue = isV2Enabled ? payloadItems : vin;

        esntialEligibilityFetcher(eligibilityURL, isV2Enabled ? payloadItems : vin)
            .then((response) => {
                setIsLoading(false);

                if (response?.totalMatched >= 1 || response?.[0]?.sellable) {
                    // Enable ecommWebsite and dealerPreferredCTA
                    dispatch(inventoryDuck.creators.updateInventoryItem(id, {
                        esntial,
                        ecommWebsite: esntial.ecommWebsite,
                        owner: {
                            ...listing.owner,
                            dealerPreferredCTA: esntial.deemphasized ? undefined : 'WEBSITE',
                            ...(isSponsoredExperience && { hideEmailForm: true }),
                        },
                    }));

                    setIsEligible(true);

                    // Analytics tracking
                    if (window?.newrelic) {
                        window.newrelic.addPageAction('ThirdPartyAction', {
                            id,
                            [dataKey]: dataValue,
                            component: 'useEsntialEligibilitySRP',
                            message: `Vehicle eligible for SRP Esntial${versionSuffix}`,
                        });
                    }
                } else {
                    let message = 'Vehicle ineligible for SRP Esntial';
                    if (response?.circuitBreakerOpen) {
                        message = 'Esntial Eligibility Circuit Breaker Open; displaying as vehicle ineligible for Esntial';
                    }

                    setError(message);

                    // Analytics tracking
                    if (window?.newrelic) {
                        window.newrelic.addPageAction('ThirdPartyError', {
                            id,
                            [dataKey]: dataValue,
                            component: 'useEsntialEligibilitySRP',
                            message: `${message}${versionSuffix}`,
                        });
                    }
                }
            })
            .catch((err) => {
                setIsLoading(false);
                setError(err.message || 'Eligibility check failed');
                // eslint-disable-next-line no-console
                console.error('Esntial eligibility check failed:', err);
            });

    }, [
        vin,
        existingDeal.isEcommerce,
        isSponsoredExperience,
        esntial?.ecommWebsite,
        listing?.id,
        enableV2,
        dispatch,
        esntial,
        id,
        listing,
    ]);

    return { isEligible, isLoading, error };
}
